/* =====================================================================
 * @auw/plugins — resolvePluginCatalog
 *
 * Takes the raw `listPlugins()` response + tenant-level overrides and
 * produces a single ordered `PluginCatalogEntry[]` the UI can render.
 *
 * Why resolve in a helper (not inline in the component):
 *   - Guarantees the six separate lookup tables that used to live in
 *     PluginModal.tsx (labels/meta/catalog/tools/scope/mcp) are merged
 *     with the same priority rules everywhere they're consumed.
 *   - Lets enterprise tenants reorder, rename, or hide plugins without
 *     forking UI code — the app renders whatever the resolver returns.
 *
 * Priority rules (first match wins, per field):
 *   1. Tenant `overrides[id]` (label / meta.* / catalog.* / category)
 *   2. Builtin descriptor
 *   3. Backend-provided display name (only used as the label for
 *      descriptor-less custom plugins; never overrides a builtin)
 *
 * Filtering rules:
 *   - Plugin ids in `hiddenPluginIds` are dropped early, before
 *     descriptor merging.
 *   - `overrides[id].hidden === true` also drops the plugin.
 *
 * Ordering rules:
 *   - If `order` is provided, entries matching those ids appear first
 *     in that order (unknown ids in `order` are skipped).
 *   - Remaining entries keep the source order of `backendPlugins`.
 * ===================================================================== */

import { BUILTIN_PLUGIN_MAP } from './descriptors';
import type {
  BackendPluginInfo,
  PluginCatalogEntry,
  PluginCatalogInfo,
  PluginCatalogInput,
  PluginDescriptor,
  PluginMeta,
  PluginId,
  TenantPluginOverride,
} from './types';

/** Generic fallback descriptor used when the backend exposes a plugin
 *  id that has no builtin descriptor (custom MCP servers, preview
 *  integrations, enterprise-private connectors…). The UI still gets a
 *  usable row with tenant-safe defaults. */
const FALLBACK_META: PluginMeta = {
  logoSrc: '',
  logoAlt: 'plugin',
  accentColor: '#6B7280',
  accentBackground: 'rgba(107, 114, 128, 0.1)',
};

const FALLBACK_CATALOG: PluginCatalogInfo = {
  categoryKey: 'pluginDetail.categories.productivity',
  featuresKey: 'pluginDetail.features.default',
  descriptionKey: 'pluginDetail.descriptions.default',
  developer: 'askyour.ai',
};

function buildFallbackDescriptor(
  backend: BackendPluginInfo,
): PluginDescriptor {
  return {
    id: backend.id,
    label: backend.name || backend.id,
    category: 'productivity',
    meta: FALLBACK_META,
    catalog: FALLBACK_CATALOG,
    tools: [],
  };
}

/** Apply a single tenant override on top of a builtin/fallback
 *  descriptor. Returns a brand-new descriptor so callers can treat the
 *  builtin map as immutable. */
function applyOverride(
  base: PluginDescriptor,
  override: TenantPluginOverride | undefined,
): PluginDescriptor {
  if (!override) return base;

  const nextMeta: PluginMeta = override.meta
    ? { ...base.meta, ...override.meta }
    : base.meta;

  const nextCatalog: PluginCatalogInfo = override.catalog
    ? { ...base.catalog, ...override.catalog }
    : base.catalog;

  return {
    ...base,
    label: override.label ?? base.label,
    category: override.category ?? base.category,
    meta: nextMeta,
    catalog: nextCatalog,
    ux: override.ux ? { ...(base.ux ?? {}), ...override.ux } : base.ux,
  };
}

/**
 * Resolve the plugin catalog for the current tenant + backend snapshot.
 * Pure / side-effect free; safe to call inside `useMemo`.
 */
export function resolvePluginCatalog(
  input: PluginCatalogInput,
): PluginCatalogEntry[] {
  const {
    backendPlugins,
    hiddenPluginIds,
    order,
    overrides,
  } = input;

  const hiddenSet = new Set<string>(hiddenPluginIds ?? []);

  const entries: PluginCatalogEntry[] = [];
  const seen = new Set<string>();

  for (const backend of backendPlugins) {
    if (hiddenSet.has(backend.id)) continue;

    const override = overrides?.[backend.id];
    if (override?.hidden) continue;

    const builtin = BUILTIN_PLUGIN_MAP[backend.id];
    const base: PluginDescriptor = builtin ?? buildFallbackDescriptor(backend);
    const descriptor = applyOverride(base, override);

    entries.push({
      descriptor,
      backend,
      connected: Boolean(backend.connected),
    });
    seen.add(backend.id);
  }

  if (!order || order.length === 0) {
    return entries;
  }

  const byId = new Map<string, PluginCatalogEntry>();
  for (const entry of entries) {
    byId.set(entry.descriptor.id, entry);
  }

  const ordered: PluginCatalogEntry[] = [];
  const consumed = new Set<string>();

  for (const id of order) {
    const entry = byId.get(id);
    if (entry && !consumed.has(id)) {
      ordered.push(entry);
      consumed.add(id);
    }
  }

  for (const entry of entries) {
    if (!consumed.has(entry.descriptor.id)) {
      ordered.push(entry);
    }
  }

  return ordered;
}

/**
 * Lookup helper for surfaces that render a vendor logo OUTSIDE the
 * plugin catalog flow — typically the Integrated Agent message
 * input's "+" attachment dropdown, where each vendor row shows the
 * service icon next to a one-line label.
 *
 * Resolution order (first match wins):
 *   1. Tenant override (`overrides[id].meta.logoSrc`)
 *   2. Builtin descriptor (`BUILTIN_PLUGIN_MAP[id].meta.logoSrc`)
 *   3. `null` — caller renders an icon-font fallback.
 *
 * Returning `null` for unknown ids means the dropdown can degrade to
 * a `lucide` icon without throwing, which keeps the UI usable on
 * tenants that intentionally narrow the plugin surface (e.g. Samsung
 * shipping `enabled: true` only for a subset).
 *
 * Pure / side-effect free; safe to call inside `useMemo`.
 */
export function resolvePluginLogoSrc(
  overrides:
    | Readonly<Record<string, TenantPluginOverride>>
    | undefined,
  vendorId: PluginId | string,
): string | null {
  const override = overrides?.[vendorId];
  if (override?.meta?.logoSrc) return override.meta.logoSrc;

  const builtin = BUILTIN_PLUGIN_MAP[vendorId];
  if (builtin?.meta?.logoSrc) return builtin.meta.logoSrc;

  return null;
}
